import { FinishT } from '@dora/backpack/src/configurableProperties';
import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as Hooks from 'preact/hooks';
import { CatalogueEntry } from '../../../data/catalogueEntry';
import * as Pr from '../../../data/product';
import * as Selectors from '../../../store/selectors';
import { Interface } from '../../arya';
import { useDispatch, useSelector } from '../../hooks/redux';
import { useFurnitureList } from '../../hooks/useFurnitureList';

export const useUpdateFurnitureInScene = (
    aryaRef: O.Option<Interface>,
    catalogueEntries: CatalogueEntry[],
) => {
    const productsByGroupingCode = useSelector(Selectors.ProductData.productsByGroupingCode);
    const currentGlobalConfigurablePropertySelections = useSelector(
        Selectors.ConfigurableProperties.currentPropertySelections,
    );
    const currentGlobalConfigurableProperties = useSelector(
        Selectors.ConfigurableProperties.configurableProperties,
    );
    const productBySku = useSelector(Selectors.ProductData.productBySkuFn);
    const furnitureList = useFurnitureList(aryaRef);
    const range = useSelector(Selectors.Core.range);
    const dispatch = useDispatch();
    const [loading, setLoading] = Hooks.useState(false);
    const [unavailable, setUnavailable] = Hooks.useState<Record<string, FinishT[]>>({});
    const [furnitureToRemove, setFurnitureToRemove] = Hooks.useState<string[]>([]);
    const [furnitureToAdd, setFurnitureToAdd] = Hooks.useState<string[]>([]);
    const [skusInScene, setSkusInScene] = Hooks.useState<string[]>([]);

    const checkSkusInFurniture = (skus: string[]) => {
        const alreadyChecked = skus.every(s => skusInScene.includes(s));
        if (alreadyChecked) {
            return false;
        } else {
            setSkusInScene(prev => [...prev, ...skus]);
            return true;
        }
    };
    /**
     * This hook monitors the current furniture against the global colour state
     * marking any furniture that isn't available in any colour options
     */
    Hooks.useEffect(() => {
        // Filter out products that have already been checked and added to skusInScene so we aren't checking multiple times for the same product
        const furnitureToUpdate = furnitureList.filter(
            f =>
                f.products.some(p => p.configurableProperties !== undefined) &&
                checkSkusInFurniture(skusInScene),
        );

        if (
            productBySku &&
            furnitureToUpdate.length > 0 &&
            currentGlobalConfigurableProperties !== null &&
            currentGlobalConfigurableProperties.length !== 0
        ) {
            // For each property set in global property state
            const unavailable = currentGlobalConfigurableProperties.reduce(
                (unavailable, property) => {
                    // check every option available for that property
                    property.options.forEach(option => {
                        // check if any furniture in the scene is unavailable in this option
                        furnitureToUpdate.forEach(furniture => {
                            if (O.isNone(furniture.configuration)) {
                                const entry = catalogueEntries.find(e =>
                                    furniture.products.every(product =>
                                        e.furnitureDescription.variants
                                            .map(v => v.canonical_id)
                                            .includes(Pr.productId(product)),
                                    ),
                                );

                                if (entry) {
                                    entry.furnitureDescription.variants.forEach(variant => {
                                        const product = productBySku(variant.canonical_id);
                                        if (
                                            product?.groupingCode &&
                                            product?.properties[property.propertyKey] !== undefined
                                        ) {
                                            const productInOption = productsByGroupingCode(
                                                product.groupingCode,
                                            )?.find(
                                                p =>
                                                    (p.properties[property.propertyKey] as FinishT)
                                                        ?.name === option.name,
                                            );

                                            if (productInOption === undefined) {
                                                // mark this option as having unavailable products for this property
                                                // TODO: we may need to store the furniture reference if we want to display a pop-up with more details
                                                unavailable[property.propertyKey]
                                                    ? unavailable[property.propertyKey].push(option)
                                                    : (unavailable[property.propertyKey] = [
                                                          option,
                                                      ]);
                                            }
                                        }
                                    });
                                }
                            }
                        });
                    });
                    return unavailable;
                },
                {} as Record<string, FinishT[]>,
            );
            setUnavailable(unavailable);
        }
    }, [currentGlobalConfigurableProperties, furnitureList]);

    // This hook handles the loading state of the global configurable properties drawer
    Hooks.useEffect(() => {
        if (furnitureToAdd.length === 0 && furnitureToRemove.length === 0) {
            setLoading(false);
        } else {
            setLoading(true);
            furnitureList.forEach(f => {
                if (furnitureToAdd.includes(f.aryaId)) {
                    setFurnitureToAdd(prev => prev.filter(p => p !== f.aryaId));
                }
            });
        }
    }, [furnitureToAdd, furnitureList]);

    /**
     * This hook responds to changes in the Global Colour state, updating furniture in the scene
     * to match the current global colour properties. If no appropriate option is available, the
     * furniture is removed anyway as a warning will be shown beforehand.
     */
    Hooks.useEffect(() => {
        const furnitureToUpdate = furnitureList.filter(f =>
            f.products.some(p => p.configurableProperties !== undefined),
        );

        if (productBySku && range) {
            furnitureToUpdate.forEach(furniture => {
                if (!furnitureToRemove.includes(furniture.aryaId)) {
                    setLoading(true);
                    setFurnitureToRemove(prev => [...prev, furniture.aryaId]);
                    pipe(
                        O.Do,
                        O.bind('arya', () => aryaRef),
                        O.bind('pose', ({ arya }) => arya.furniturePose(furniture.aryaId)),
                        O.map(({ arya, pose }) => {
                            if (O.isSome(furniture.configuration)) {
                                arya.addModularSofa(
                                    furniture.configuration.value,
                                    pose,
                                    range,
                                    'user_change_colour',
                                ).then(id => {
                                    if (id) {
                                        arya.removeModularSofa(furniture.aryaId);
                                        setFurnitureToRemove(prev =>
                                            prev.filter(p => p !== furniture.aryaId),
                                        );
                                        setFurnitureToAdd(prev => [...prev, id]);
                                    }
                                });
                            } else {
                                pipe(
                                    O.Do,
                                    O.bind('originalEntry', () =>
                                        O.fromNullable(
                                            catalogueEntries.find(e => {
                                                const productsInEntry =
                                                    e.furnitureDescription.variants.map(
                                                        v => v.canonical_id,
                                                    );
                                                return furniture.products.every(product =>
                                                    productsInEntry.includes(Pr.productId(product)),
                                                );
                                            }),
                                        ),
                                    ),
                                    O.bind('productsInEntry', ({ originalEntry }) =>
                                        O.fromNullable(
                                            originalEntry.furnitureDescription.variants.map(v =>
                                                productBySku(v.canonical_id),
                                            ),
                                        ),
                                    ),
                                    O.bind('entryInNewColour', ({ productsInEntry }) => {
                                        const groupingCodes = productsInEntry.map(
                                            p => p?.groupingCode,
                                        );

                                        return O.fromNullable(
                                            catalogueEntries.find(entry => {
                                                return currentGlobalConfigurablePropertySelections.every(
                                                    prop => {
                                                        const productsInEntry =
                                                            entry.furnitureDescription.variants.map(
                                                                v => productBySku(v.canonical_id),
                                                            );
                                                        const groupingCodesMatch = productsInEntry
                                                            .map(p => p?.groupingCode)
                                                            .every(gc =>
                                                                groupingCodes.includes(gc),
                                                            );
                                                        if (groupingCodesMatch) {
                                                            return productsInEntry.every(
                                                                prod =>
                                                                    (
                                                                        prod?.properties[
                                                                            prop.propertyKey
                                                                        ] as FinishT
                                                                    )?.name === prop.finish.name ||
                                                                    prod?.properties[
                                                                        prop.propertyKey
                                                                    ] === undefined,
                                                            );
                                                        } else {
                                                            return false;
                                                        }
                                                    },
                                                );
                                            }),
                                        );
                                    }),
                                    O.fold(
                                        () => {
                                            // no entry found means this furniture is unavailable in this option
                                            // a warning has been shown so we remove the furniture
                                            arya.removeFurniture(furniture.aryaId);
                                            setFurnitureToRemove(prev =>
                                                prev.filter(p => p !== furniture.aryaId),
                                            );
                                        },
                                        ({ entryInNewColour, originalEntry }) => {
                                            const newFurnitureDescription =
                                                entryInNewColour.furnitureDescription;

                                            const alreadyCorrect =
                                                entryInNewColour.furnitureDescription.variants.every(
                                                    v =>
                                                        originalEntry.furnitureDescription.variants
                                                            .map(v => v.canonical_id)
                                                            .includes(v.canonical_id),
                                                );
                                            if (!alreadyCorrect) {
                                                // add a furniture according to the new catalogue entry furniture description
                                                arya.addFurniture(
                                                    newFurnitureDescription,
                                                    entryInNewColour.summary,
                                                    'user_change_colour',
                                                    pose,
                                                ).then(furnitureId => {
                                                    if (furnitureId) {
                                                        setFurnitureToAdd(prev => [
                                                            ...prev,
                                                            furnitureId,
                                                        ]);

                                                        arya.removeFurniture(furniture.aryaId);
                                                        setFurnitureToRemove(prev =>
                                                            prev.filter(
                                                                p => p !== furniture.aryaId,
                                                            ),
                                                        );
                                                    }
                                                });
                                            } else {
                                                setFurnitureToRemove(prev =>
                                                    prev.filter(p => p !== furniture.aryaId),
                                                );
                                            }
                                        },
                                    ),
                                );
                            }
                        }),
                    );
                }
            });
        }
    }, [currentGlobalConfigurablePropertySelections]);

    return { loading, unavailable };
};
